import React, { useEffect, useState, Fragment } from 'react';
import { doc, updateDoc, query, where, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { toast } from 'react-toastify';
import { Dialog, Transition } from '@headlessui/react';
import { UserCircleIcon, TruckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const formatDate = (date) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
  return new Date(date).toLocaleString(undefined, options);
};

const JobHeader = ({ jobCard }) => {
  const [isFullyPaid, setIsFullyPaid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exitMileage, setExitMileage] = useState('');
  const [jobClosureComments, setJobClosureComments] = useState('');
  const [nextServiceMileage, setNextServiceMileage] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const entryMileage = jobCard?.entryMileage || '';

  useEffect(() => {
    if (jobCard?.jobCardNo) {
      const q = query(collection(db, 'newInvoices'), where('jobCardNo', '==', jobCard.jobCardNo));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const invoices = snapshot.docs.map(doc => doc.data());
        const fullyPaidInvoice = invoices.find(invoice => invoice.invoiceStatus === 'Fully Paid');
        setIsFullyPaid(!!fullyPaidInvoice);
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, [jobCard?.jobCardNo]);

  const handleCloseJob = async () => {
    if (parseInt(exitMileage) < parseInt(entryMileage)) {
      toast.error('Exit mileage cannot be lower than entry mileage.');
      return;
    }

    try {
      const jobRef = doc(db, 'jobs', jobCard.id);
      await updateDoc(jobRef, {
        exitMileage,
        jobClosureComments,
        nextServiceMileage,
        deliveryDate: new Date(deliveryDate),
        status: 'Closed',
      });
      toast.success('Job closed successfully');
      setIsModalOpen(false);
    } catch (error) {
      logger.error('Error closing job:', error);
      toast.error('Failed to close job');
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Customer Info Card */}
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center mb-3">
            <UserCircleIcon className="h-8 w-8 text-blue-600 mr-2" />
            <h2 className="text-lg font-black text-gray-800">Customer Information</h2>
          </div>
          <div className="space-y-1">
            <p className="text-sm">
              <span className="font-bold text-gray-700">Name:</span>{' '}
              <span className="text-gray-900">{jobCard?.customerName}</span>
            </p>
            <p className="text-sm">
              <span className="font-bold text-gray-700">Email:</span>{' '}
              <span className="text-blue-600">{jobCard?.customerEmail}</span>
            </p>
            <p className="text-sm">
              <span className="font-bold text-gray-700">Vehicle:</span>{' '}
              <span className="text-gray-900">
                {jobCard?.vehicleMake} {jobCard?.vehicleModel} ({jobCard?.registrationNumber})
              </span>
            </p>
            <p className="text-sm">
              <span className="font-bold text-gray-700">VIN:</span>{' '}
              <span className="text-gray-600">{jobCard?.VIN}</span>
            </p>
            <p className="text-sm">
              <span className="font-bold text-gray-700">Entry Mileage:</span>{' '}
              <span className="text-gray-900">{jobCard?.entryMileage}</span>
            </p>
          </div>
        </div>

        {/* Job Card Info */}
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-indigo-500">
          <div className="flex items-center mb-3">
            <TruckIcon className="h-8 w-8 text-indigo-600 mr-2" />
            <h2 className="text-lg font-black text-gray-800">Job Card Details</h2>
          </div>
          <div className="space-y-1">
            <p className="text-sm">
              <span className="font-bold text-gray-700">Job Card #:</span>{' '}
              <span className="text-gray-900 font-bold">{jobCard?.jobCardNo}</span>
            </p>
            <p className="text-sm">
              <span className="font-bold text-gray-700">Date Posted:</span>{' '}
              <span className="text-gray-600">{formatDate(jobCard?.createdAt.toDate())}</span>
            </p>
            <p className="text-sm flex items-center">
              <span className="font-bold text-gray-700 mr-2">Status:</span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold ${
                  jobCard?.status === 'Open' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {jobCard?.status}
              </span>
            </p>
            <div className="pt-2">
              <button
                disabled={!isFullyPaid || loading}
                onClick={() => setIsModalOpen(true)}
                className={`w-full py-2 px-4 font-bold rounded-xl transition-all ${
                  isFullyPaid && !loading
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {loading ? 'Checking Status...' : isFullyPaid ? 'Close Job' : 'Payment Required to Close'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Close Job Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
                  {/* Modal Header */}
                  <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between">
                    <Dialog.Title className="text-lg font-bold text-white">Close Job Card</Dialog.Title>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="text-white hover:text-gray-200 transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Exit Mileage</label>
                      <input
                        type="number"
                        value={exitMileage}
                        onChange={(e) => setExitMileage(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all text-sm"
                        placeholder="Enter exit mileage"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Job Closure Comments</label>
                      <textarea
                        value={jobClosureComments}
                        onChange={(e) => setJobClosureComments(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all text-sm"
                        placeholder="Enter any closing comments"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Next Service Mileage</label>
                      <input
                        type="number"
                        value={nextServiceMileage}
                        onChange={(e) => setNextServiceMileage(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all text-sm"
                        placeholder="Enter next service mileage"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Delivery Date</label>
                      <input
                        type="date"
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all text-sm"
                      />
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="bg-gray-50 px-6 py-4 flex gap-3">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="flex-1 px-4 py-2 bg-gray-600 text-white font-bold rounded-xl hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCloseJob}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-xl hover:from-red-700 hover:to-red-800 transition-all"
                    >
                      Confirm Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default JobHeader;
